import { useMemo } from 'react';
import {
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { keyBy, keysOf, toggleElementInArray } from 'utils/arrayHelpers';

import { useUserKey } from '../../../hooks';
import { useEventAggregatesQuery } from '../../hooks/useEventAggregatesQuery';
import { SEARCH_PARAM_KEYS } from '../constants';
import { getBrowser, getDevice, getOS } from '../utils';
import { EventHistoryFiltersContext } from './EventHistoryFiltersContext';
import { useUserAgentsQuery } from '../hooks/useUserAgentsQuery';
import { CustomEventName } from '../../../domain';

const isDefined = (input: string | undefined) => !!input;

/**
 * Helper hook to create a persistent state for a filter, including toggle/clear functions.
 */
const useFiltersState = (
  searchParamKey: string,
): [filters: string[], toggleFilter: (filter: string) => void, clearFilters: () => void] => {
  const [filters, setFilters] = useSearchParamState({
    searchParamKey,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const toggleFilter = (filter: string) => setFilters(toggleElementInArray(filter, filters));
  const clearFilters = () => setFilters([]);
  return [filters, toggleFilter, clearFilters];
};

export const EventHistoryFiltersContextProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  // Queries for filter options
  const userKey = useUserKey();

  const { data: userAgentsData, isLoading: isLoadingUserAgents } = useUserAgentsQuery(userKey);

  const {
    data: { UserProfileEvents: eventData = [] } = {},
    isLoading: isLoadingEvents,
  } = useEventAggregatesQuery();
  const events = eventData.map(({ eventName }) => eventName);

  // Filter state
  const [eventFilters, toggleFilterForEvent, clearEventTypeFilters] = useFiltersState(
    SEARCH_PARAM_KEYS.eventType,
  );
  const systemEventFilters = eventData
    .filter(
      ({ eventName, eventType }) => eventType === 'System' && eventFilters.includes(eventName),
    )
    .map(({ eventName }) => eventName);
  const customEventFilters = eventData
    .filter(
      ({ eventType }) =>
        eventType === 'Custom' && eventFilters.includes(CustomEventName.customEvent),
    )
    .map(() => CustomEventName.customEvent);

  const eventTypeFilters = useMemo(
    () => [...systemEventFilters, ...customEventFilters],
    [eventData, eventFilters],
  );

  const [browserFilters, toggleFilterForBrowser, clearBrowserFilters] = useFiltersState(
    SEARCH_PARAM_KEYS.browser,
  );
  const [operatingSystemFilters, toggleFilterForOperatingSystem, clearOperatingSystemFilters] =
    useFiltersState(SEARCH_PARAM_KEYS.operatingSystem);
  const [deviceFilters, toggleFilterForDevice, clearDeviceFilters] = useFiltersState(
    SEARCH_PARAM_KEYS.device,
  );
  const [search, setSearch] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.search,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  // Callbacks
  const clearFilters = () => {
    clearEventTypeFilters();
    clearBrowserFilters();
    clearOperatingSystemFilters();
    clearDeviceFilters();
    setSearch('');
  };

  const userAgentState = useMemo(() => {
    const userAgents = userAgentsData?.map(({ userAgent }) => userAgent) ?? [];
    const userAgentsByBrowser = keyBy(userAgents, getBrowser);
    const userAgentsByOS = keyBy(userAgents, getOS);
    const userAgentsByDevice = keyBy(userAgents, getDevice);
    const browsers = keysOf(userAgentsByBrowser);
    const operatingSystems = keysOf(userAgentsByOS);
    const devices = keysOf(userAgentsByDevice);
    const userAgentFilters = [
      ...new Set([
        ...browserFilters.flatMap((browser) => userAgentsByBrowser[browser]).filter(isDefined),
        ...operatingSystemFilters.flatMap((os) => userAgentsByOS[os]).filter(isDefined),
        ...deviceFilters.flatMap((device) => userAgentsByDevice[device]).filter(isDefined),
      ]),
    ];
    return {
      browsers,
      operatingSystems,
      devices,
      userAgentFilters,
    };
  }, [userAgentsData, browserFilters, operatingSystemFilters, deviceFilters]);

  return (
    <EventHistoryFiltersContext.Provider
      value={{
        isLoadingEvents,
        events,
        eventTypeFilters,
        eventFilters,
        isLoadingUserAgents,
        browserFilters,
        operatingSystemFilters,
        deviceFilters,
        search,
        ...userAgentState,
        toggleFilterForEvent,
        toggleFilterForBrowser,
        toggleFilterForOperatingSystem,
        toggleFilterForDevice,
        setSearch,
        clearFilters,
      }}
    >
      {children}
    </EventHistoryFiltersContext.Provider>
  );
};
